import type { InvoiceData, LineItem } from "@/apis/gql/types";
import { CostCenterCell } from "@/components/shared/cells/cost-center";
import type { FormValues as CostCenterFormValues } from "@/components/shared/form/lineItems/cost-center";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import { useUpdateManyLineItems } from "@/queries/line-items/update";
import { useCallback } from "react";

type Props = {
  invoice: Pick<
    InvoiceData,
    "status" | "costCenterIds" | "invoiceId" | "splitPdf"
  >;
  lineItems: Pick<LineItem, "lineItemId">[];
  showConfidence?: boolean;
};

export const CostCentersCell = ({ invoice, lineItems }: Props) => {
  const { fetchInvoices } = useGetAllInvoices({
    enabled: false,
    skipDbClear: true,
  });
  const updateLineItems = useUpdateManyLineItems({
    disableOptimisticResponse: true,
  });

  const handleRefreshRow = useCallback(
    async (values: CostCenterFormValues) => {
      await updateLineItems(
        lineItems.map((lineItem) => lineItem.lineItemId),
        {
          costCenterId: values.costCenterId,
        },
      );
      fetchInvoices({
        query: {
          invoiceId: {
            value: [invoice.invoiceId],
            op: "in",
          },
        },
      });
    },
    [invoice.invoiceId, fetchInvoices, lineItems, updateLineItems],
  );

  const costCenterIds = invoice.costCenterIds;
  const lineItemsIds = lineItems.map((lineItem) => lineItem.lineItemId);

  if (
    invoice.status === InvoiceStatus.NEW ||
    invoice.status === InvoiceStatus.ERROR ||
    invoice.splitPdf === true
  ) {
    return null;
  }

  return (
    <CostCenterCell
      lineItemIds={lineItemsIds}
      costCenterIds={costCenterIds}
      onChange={handleRefreshRow}
    />
  );
};
